import { drawWagerStartFlash } from "./wagerFlashOverlay";
import { COMPOSITE_FONT_FAMILY } from "./compositeFonts";

/** Format smartphone 9:16 */
export const COMPOSITE_WIDTH = 720;
export const COMPOSITE_HEIGHT = 1280;

/** Chrono en bas au centre — rangée de dots (pas de chiffres). */
export const COMPOSITE_TIMER_LAYOUT = {
  y: COMPOSITE_HEIGHT - 200,
  dotR: 11,
  gap: 18,
};

/** Badge Q + cartouche — calés sur .mf-play-badge / .mf-question-card */
export const COMPOSITE_QUESTION_LAYOUT = {
  padX: 28,
  padY: 36,
  badgeH: 34,
  badgePadX: 16,
  gapBelowBadge: 14,
  cardPadX: 22,
  cardPadY: 18,
  radius: 16,
};
/** Fond jeu (tailwind md-navy) — zones transparentes de la capture DOM */
export const COMPOSITE_BG_NAVY = "#061224";
/**
 * Opacité du calque plateau (comme l’ancien html-to-image).
 * Appliqué d’abord en voile navy plein écran, puis sur banque / trappes / totaux.
 */
export const COMPOSITE_UI_OPACITY = 0.55;

/** Voile navy plein cadre — le « verre bleuté » sur toute la vidéo (ex-fond capture DOM). */
export function drawCompositeGlassWash(ctx, w, h, alpha = COMPOSITE_UI_OPACITY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = COMPOSITE_BG_NAVY;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

/** Layout trappes (héritage Money Drop — inutilisé en MF). */
const TRAP_LAYOUT = {
  doorW: 200,
  doorH: 200,
  gap: 10,
  bottomPad: 22,
  letterR: 18,
  letterGap: 10,
};

const LETTERS = ["A", "B", "C", "D"];

function formatMoneyCanvas(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0 €";
  return new Intl.NumberFormat("fr-FR").format(num) + " €";
}

/** Banque peinte sur le canvas (fiable, comme la question). */
export function drawCompositeBank(ctx, w, h, { bank, show = true } = {}) {
  if (!show || bank == null) return;
  const x = 20;
  const y = 10;
  ctx.save();
  ctx.globalAlpha = COMPOSITE_UI_OPACITY;
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = `600 11px ${COMPOSITE_FONT_FAMILY}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("BANQUE ACTUELLE", x, y);
  ctx.shadowColor = "rgba(255,255,255,0.45)";
  ctx.shadowBlur = 10;
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold 28px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(formatMoneyCanvas(bank), x, y + 16);
  ctx.restore();
}

/**
 * Trappes sans fond navy (le voile plein écran suffit) :
 * badge + bordure cyan + réponse centrée + mise en bas.
 */
export function drawCompositeTraps(ctx, w, h, live = {}) {
  const answers = live.answers || [];
  const visible = Math.min(
    Number(live.visibleAnswerCount || 0),
    answers.length || 4,
    4,
  );
  if (visible <= 0) return;

  const wagers = live.wagers || [];
  const revealed = new Set(live.revealedDoors || []);
  const correctIndex = live.correctIndex ?? -1;
  const correctHighlighted = Boolean(live.correctHighlighted);
  const phase = live.phase || "";

  const { doorW, doorH, gap, bottomPad, letterR, letterGap } = TRAP_LAYOUT;
  const totalW = doorW * 4 + gap * 3;
  const startX = (w - totalW) / 2;
  const doorY = h - bottomPad - doorH;
  const radius = 22;
  const wagerReserve = 44;

  for (let i = 0; i < visible; i += 1) {
    const x = startX + i * (doorW + gap);
    const isDropped = phase === "dropping" && revealed.has(i);
    const isWinner = phase === "dropping" && correctHighlighted && i === correctIndex;
    const dim = isDropped && !isWinner;
    const doorAlpha = dim ? COMPOSITE_UI_OPACITY * 0.28 : COMPOSITE_UI_OPACITY;

    const bx = x + doorW / 2;
    const by = doorY - letterGap - letterR;
    const wager = Number(wagers[i] || 0);

    // Badge lettre
    ctx.save();
    ctx.globalAlpha = doorAlpha;
    ctx.beginPath();
    ctx.arc(bx, by, letterR, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(79,195,247,0.18)";
    ctx.fill();
    ctx.strokeStyle = "rgba(79,195,247,0.55)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = "#4FC3F7";
    ctx.font = `bold 18px ${COMPOSITE_FONT_FAMILY}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(LETTERS[i] || String(i + 1), bx, by);
    ctx.restore();

    // Cadre uniquement — pas de fond bleu (voile global déjà appliqué)
    ctx.save();
    ctx.globalAlpha = doorAlpha;
    ctx.beginPath();
    ctx.roundRect(x, doorY, doorW, doorH, radius);
    ctx.shadowColor = "rgba(79,195,247,0.45)";
    ctx.shadowBlur = 28;
    ctx.lineWidth = 4;
    ctx.strokeStyle = isWinner ? "rgba(255,255,255,0.95)" : "rgba(79,195,247,0.85)";
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = doorAlpha;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const answer = String(answers[i] || "");
    if (answer) {
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 16px ${COMPOSITE_FONT_FAMILY}`;
      const lines = wrapCanvasLines(ctx, answer, doorW - 28).slice(0, 5);
      const lineH = 20;
      const blockH = lines.length * lineH;
      // Zone réponse = hauteur porte moins la rangée mise (comme TrapDoor grid)
      const contentBottom = doorY + doorH - (wager > 0 ? wagerReserve : 16);
      const contentTop = doorY + 12;
      const midY = contentTop + (contentBottom - contentTop) / 2;
      let ty = midY - blockH / 2 + lineH / 2;
      for (const line of lines) {
        ctx.fillText(line, bx, ty);
        ty += lineH;
      }
    }

    if (wager > 0) {
      ctx.shadowColor = "rgba(255,255,255,0.55)";
      ctx.shadowBlur = 12;
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 20px ${COMPOSITE_FONT_FAMILY}`;
      ctx.fillText(formatMoneyCanvas(wager), bx, doorY + doorH - 28);
    }
    ctx.restore();
  }
}

/** Totaux calés comme le CSS capture (à gauche de A / droite de D). */
export function drawCompositeTotals(ctx, w, h, live = {}) {
  if (!live.showTotals) return;
  const { doorW, gap, bottomPad, doorH } = TRAP_LAYOUT;
  const totalW = doorW * 4 + gap * 3;
  const half = totalW / 2;
  const mid = w / 2;
  const baseline = h - bottomPad - 8;

  ctx.save();
  ctx.globalAlpha = COMPOSITE_UI_OPACITY;
  ctx.textBaseline = "bottom";

  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = `600 11px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText("TOTAL MISÉ", mid - half - 12, baseline - 28);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold 22px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(formatMoneyCanvas(live.totalWager), mid - half - 12, baseline);

  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = `600 11px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText("RESTANT", mid + half + 12, baseline - 28);
  ctx.fillStyle = "#4FC3F7";
  ctx.font = `bold 22px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(formatMoneyCanvas(live.remaining), mid + half + 12, baseline);

  ctx.restore();
}

export function applyCanvasDrawQuality(ctx) {
  if (!ctx) return;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
}

export function createCompositeCanvas() {
  const canvas = document.createElement("canvas");
  canvas.width = COMPOSITE_WIDTH;
  canvas.height = COMPOSITE_HEIGHT;
  const ctx = canvas.getContext("2d", { alpha: false });
  applyCanvasDrawQuality(ctx);
  return { canvas, ctx };
}

export function drawCover(ctx, video, w, h, { mirror = false, fit = "cover" } = {}) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;

  const scale = fit === "contain" ? Math.min(w / vw, h / vh) : Math.max(w / vw, h / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  ctx.save();
  if (mirror) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, w - dx - dw, dy, dw, dh);
  } else {
    ctx.drawImage(video, dx, dy, dw, dh);
  }
  ctx.restore();
}

/**
 * Dessine la webcam en PAYSAGE pleine largeur dans un panneau (hauteur = masque).
 * Le panneau a le ratio natif de la cam — aucun crop horizontal.
 */
export function landscapePanelSize(video, maskH) {
  const vw = video?.videoWidth || 16;
  const vh = video?.videoHeight || 9;
  const panelH = maskH;
  const panelW = panelH * (vw / Math.max(1, vh));
  return { panelW, panelH };
}

export function drawLandscapePanel(ctx, video, panelW, panelH, { mirror = false } = {}) {
  if (!video?.videoWidth) return;
  // Remplit exactement le panneau paysage (= toute la largeur cam)
  drawCover(ctx, video, panelW, panelH, { mirror, fit: "cover" });
}

/** Calque UI déjà normalisé 720×1280 par capturePlayScreenDom. */
export function uiDrawRect(bitmap, w, h) {
  return { dx: 0, dy: 0, dw: w, dh: h, sx: 0, sy: 0, sw: bitmap.width, sh: bitmap.height };
}

export function drawUiBitmap(ctx, bitmap, rect) {
  const { dx, dy, dw, dh, sx, sy, sw, sh } = rect;
  ctx.drawImage(bitmap, sx, sy, sw, sh, dx, dy, dw, dh);
}

/** Calque plateau par-dessus la webcam (évite multiply qui efface question / totaux clairs). */
export function drawUiLayerMultiply(ctx, bitmap, webcamVideo, w, h, alpha = COMPOSITE_UI_OPACITY) {
  if (!bitmap?.width) return;

  const rect = uiDrawRect(bitmap, w, h);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "source-over";
  drawUiBitmap(ctx, bitmap, rect);
  ctx.restore();
}

/** Dots peints sur le canvas — un point s’éteint à chaque seconde. */
export function drawCompositeTimer(ctx, w, h, secondsLeft, thinkTimeSeconds = 5) {
  const total = Math.max(1, Math.min(30, Number(thinkTimeSeconds) || 5));
  const left = Math.max(0, Math.min(total, Number(secondsLeft) || 0));
  const spent = total - left;
  const { y, dotR, gap } = COMPOSITE_TIMER_LAYOUT;
  const urgent = left <= 2;
  const totalW = total * (dotR * 2) + (total - 1) * gap;
  let x = (w - totalW) / 2 + dotR;

  ctx.save();
  for (let i = 0; i < total; i += 1) {
    const faded = i < spent;
    ctx.beginPath();
    ctx.arc(x, y, dotR, 0, Math.PI * 2);
    if (faded) {
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      ctx.fill();
    } else if (urgent) {
      ctx.shadowColor = "rgba(251,113,133,0.7)";
      ctx.shadowBlur = 14;
      ctx.fillStyle = "#fb7185";
      ctx.fill();
      ctx.shadowBlur = 0;
    } else {
      ctx.shadowColor = "rgba(232,64,96,0.55)";
      ctx.shadowBlur = 12;
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.shadowBlur = 0;
    }
    x += dotR * 2 + gap;
  }
  ctx.restore();
}

function wrapCanvasLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${line} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      lines.push(line);
      line = words[i];
    }
  }
  lines.push(line);
  return lines;
}

/** Question peinte comme le HUD live (.mf-play-badge + .mf-question-card). */
export function drawCompositeQuestion(ctx, w, h, { questionText, questionIndex = 0, questionCount = 0, show = false } = {}) {
  if (!show || !questionText) return;

  const {
    padX,
    padY,
    badgeH,
    badgePadX,
    gapBelowBadge,
    cardPadX,
    cardPadY,
    radius,
  } = COMPOSITE_QUESTION_LAYOUT;

  const qLabel = questionCount > 0 ? `Q${questionIndex + 1}/${questionCount}` : `Q${questionIndex + 1}`;

  ctx.save();

  // Badge rose (comme .mf-play-badge)
  ctx.font = `700 15px ${COMPOSITE_FONT_FAMILY}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const badgeTextW = ctx.measureText(qLabel).width;
  const badgeW = badgeTextW + badgePadX * 2;
  const badgeX = padX;
  const badgeY = padY;

  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, badgeH / 2);
  ctx.fillStyle = "rgba(232, 64, 96, 0.2)";
  ctx.fill();
  ctx.strokeStyle = "rgba(232, 64, 96, 0.45)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "#ffb3c4";
  ctx.fillText(qLabel, badgeX + badgePadX, badgeY + badgeH / 2);

  // Cartouche sombre verre (comme .mf-question-card)
  const fontSize = 24;
  ctx.font = `700 ${fontSize}px ${COMPOSITE_FONT_FAMILY}`;
  const cardX = padX;
  const cardW = w - padX * 2;
  const maxTextW = cardW - cardPadX * 2;
  const lines = wrapCanvasLines(ctx, questionText, maxTextW).slice(0, 4);
  const lineHeight = fontSize * 1.3;
  const textBlockH = Math.max(lineHeight, lines.length * lineHeight);
  const cardH = textBlockH + cardPadY * 2;
  const cardY = badgeY + badgeH + gapBelowBadge;

  ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 8;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, radius);
  ctx.fillStyle = "rgba(8, 4, 12, 0.78)";
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.14)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  let ty = cardY + cardPadY + lineHeight / 2;
  for (const line of lines) {
    ctx.fillText(line, cardX + cardW / 2, ty);
    ty += lineHeight;
  }

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

/** Overlay « Nouvelle chance » / « Dernière chance » — peint sur le canvas composite. */
export function drawCompositeChanceNotice(ctx, w, h, chanceNotice) {
  if (!chanceNotice) return;

  const isLast = chanceNotice === "last_chance";

  ctx.save();
  ctx.fillStyle = "rgba(6, 18, 36, 0.8)";
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  let y = h * 0.28;

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = `600 ${Math.round(h * 0.02)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(isLast ? "Attention" : "Pas de panique", cx, y);
  y += h * 0.1;

  ctx.font = `900 ${Math.round(h * 0.1)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillStyle = isLast ? "#f87171" : "#22d3ee";
  ctx.fillText(isLast ? "DERNIÈRE CHANCE" : "NOUVELLE CHANCE", cx, y);
  y += h * 0.12;

  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.font = `500 ${Math.round(h * 0.032)}px ${COMPOSITE_FONT_FAMILY}`;
  const body = isLast
    ? "On vous remet 100 000 € pour la dernière question. Tout se joue ici."
    : "On vous remet 100 000 € pour la question suivante. Profitez-en.";
  for (const line of wrapCanvasLines(ctx, body, w * 0.72)) {
    ctx.fillText(line, cx, y);
    y += Math.round(h * 0.045);
  }
  y += h * 0.035;

  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${Math.round(h * 0.04)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText("100 000 €", cx, y);

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

export function paintCompositeFrame(ctx, { uiBitmap: _uiBitmap, webcamVideo, flashWagerRef, liveOverlayRef }) {
  const w = COMPOSITE_WIDTH;
  const h = COMPOSITE_HEIGHT;
  const live = liveOverlayRef?.current;

  applyCanvasDrawQuality(ctx);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#1a1210";
  ctx.fillRect(0, 0, w, h);

  const panning = Boolean(live?.panning);

  if (webcamVideo?.readyState >= 2) {
    const { panelW, panelH } = landscapePanelSize(webcamVideo, h);
    const centerX = (w - panelW) / 2;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();

    if (panning) {
      const started = Number(live?.panStartedAt || 0);
      const dur = Number(live?.panDurationMs || 700) || 700;
      const rawT = started ? Math.min(1, Math.max(0, (Date.now() - started) / dur)) : 1;
      const t = 1 - Math.pow(1 - Math.min(1, rawT), 3);
      // Masque 9:16 fixe ; le paysage glisse d’un siège entier derrière (toujours miroir)
      const x = centerX - panelW * t;

      ctx.translate(x, 0);
      drawLandscapePanel(ctx, webcamVideo, panelW, panelH, { mirror: true });
      ctx.translate(panelW, 0);
      drawLandscapePanel(ctx, webcamVideo, panelW, panelH, { mirror: true });
    } else {
      ctx.translate(centerX, 0);
      drawLandscapePanel(ctx, webcamVideo, panelW, panelH, { mirror: true });
    }
    ctx.restore();
  } else {
    ctx.fillStyle = COMPOSITE_BG_NAVY;
    ctx.fillRect(0, 0, w, h);
  }

  const grad = ctx.createRadialGradient(w / 2, h * 0.4, w * 0.15, w / 2, h * 0.4, w * 0.75);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.45)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  if (live?.showQuestion && live?.questionText) {
    drawCompositeQuestion(ctx, w, h, {
      questionText: live.questionText,
      questionIndex: live.questionIndex ?? 0,
      questionCount: live.questionCount ?? 0,
      show: true,
    });
  }

  if (live?.timerShown && live.secondsLeft != null) {
    drawCompositeTimer(ctx, w, h, live.secondsLeft, live.thinkTimeSeconds);
  }

  drawWagerStartFlash(ctx, w, h, flashWagerRef);
}
